import { useCallback, useEffect, useState } from 'react';
import type { Application } from '@domain/entities';
import type { ApplicationPayload } from '@application/dto';
import {
  getListApplicationsUseCase,
  getSaveApplicationUseCase,
  getToggleApplicationStatusUseCase,
} from '@di';
import { describeError } from '@shared/utils';

export function useApplications(pageSize = 20) {
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getListApplicationsUseCase().execute({ page: 1, limit: pageSize });
      setApplications(result.data);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setIsLoading(false);
    }
  }, [pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (payload: ApplicationPayload, id?: string) => {
      setError(null);
      try {
        await getSaveApplicationUseCase().execute(payload, id);
        await load();
        return true;
      } catch (cause) {
        setError(describeError(cause));
        return false;
      }
    },
    [load]
  );

  const toggleStatus = useCallback(
    async (application: Application) => {
      setError(null);
      try {
        await getToggleApplicationStatusUseCase().execute(application);
        await load();
      } catch (cause) {
        setError(describeError(cause));
      }
    },
    [load]
  );

  return { applications, isLoading, error, reload: load, save, toggleStatus };
}
